/**
 * ToolConfigTab - Configuration panel for agent tools
 *
 * Allows users to configure:
 * - Tool name (how the agent identifies the tool)
 * - Tool description (helps the agent understand when to use the tool)
 * - Target workflow (for handoff / sub-workflow tools)
 * - Agent parameters schema (what info the agent needs to gather)
 *
 * A divider separates tool configuration from the operation's input fields.
 */

import { useCallback, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Field,
  Input,
  Textarea,
  Text,
  InfoLabel,
  Spinner,
  Divider,
} from '@coreai/fluentui-react';
import { ArrowFlowUpRightRegular } from '@fluentui/react-icons';
import { buildSerializedNodeData, useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useWorkflows } from '@/hooks/useWorkflows';
import { extractWorkflowName } from '@/types/logicApps';
import { AgentParameterEditor } from './AgentParameterEditor';
import { countAgentParameterReferences } from '@/utils/tokens';
import { resolveAgentParameterSchema } from '@/utils/agentParameterSchema';
import { SearchableCombobox, type SearchableComboboxOption } from '@/components/shared';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    width: '100%',
    minWidth: 0, // Prevent flex overflow
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXXS,
  },
  workflowFieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  workflowCombobox: {
    flex: 1,
    minWidth: 0,
  },
  workflowIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
});

interface ToolConfigTabProps {
  /** The tool name (key in the tools object) */
  toolName: string;
  /** The tool description */
  toolDescription?: string;
  /** The agent parameter schema */
  agentParameterSchema?: Record<string, unknown>;
  /** Target workflow ID (for sub-workflow / handoff tools) */
  targetWorkflowId?: string;
}

export const ToolConfigTab = ({
  toolName: initialToolName,
  toolDescription: initialToolDescription,
  agentParameterSchema: initialAgentParameterSchema,
  targetWorkflowId: initialTargetWorkflowId,
}: ToolConfigTabProps) => {
  const styles = useStyles();
  const setParameter = useNodeEditorStore((state) => state.setParameter);
  const parameterValues = useNodeEditorStore((state) => state.parameterValues);
  const isReadOnly = useNodeEditorStore((state) => state.isReadOnly);
  const hasUnappliedNodeCode = useNodeEditorStore(
    (state) => state.nodeCodeDraft?.isDirty ?? false,
  );
  const isToolNameLocked = isReadOnly || hasUnappliedNodeCode;
  
  // Read current values from parameterValues (which track edits), falling back to initial props
  const toolName = (parameterValues['_toolName'] as string | undefined) ?? initialToolName;
  const toolDescription = (parameterValues['_toolDescription'] as string | undefined) ?? initialToolDescription;
  // `null` means the user cleared every parameter; only `undefined` (no draft) falls back.
  const agentParameterSchema = resolveAgentParameterSchema(
    parameterValues['_agentParameterSchema'] as Record<string, unknown> | null | undefined,
    initialAgentParameterSchema,
  );
  const targetWorkflowId = (parameterValues['_workflowId'] as string | undefined) ?? initialTargetWorkflowId;

  // Detect if this is a sub-workflow / handoff tool
  const isSubworkflowTool = !!targetWorkflowId;

  // Fetch available workflows for the target picker
  const { data: allWorkflows = [], isLoading: isWorkflowsLoading } = useWorkflows();

  // Filter to show all workflows except the current parent workflow
  const availableWorkflows = useMemo(() => {
    return allWorkflows.map((w) => extractWorkflowName(w.name));
  }, [allWorkflows]);

  const workflowOptions = useMemo<SearchableComboboxOption[]>(
    () => availableWorkflows.map((name) => ({ value: name, text: name })),
    [availableWorkflows],
  );

  const handleToolNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setParameter('_toolName', e.target.value);
    },
    [setParameter]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setParameter('_toolDescription', e.target.value);
    },
    [setParameter]
  );

  const handleTargetWorkflowChange = useCallback(
    (value: string) => {
      if (value) {
        setParameter('_workflowId', value);
      }
    },
    [setParameter]
  );

  const handleSchemaChange = useCallback(
    (schema: Record<string, unknown>) => {
      // Store null if schema has no properties
      const hasProperties = schema.properties && Object.keys(schema.properties as object).length > 0;
      setParameter('_agentParameterSchema', hasProperties ? schema : null);
    },
    [setParameter]
  );

  // Where agentParameters('x') can appear: the edited inner action as it would
  // be serialized from the draft, plus the tool's other (unedited) actions.
  // Serializing (rather than walking parameterValues) collapses editor-only
  // aliases such as swagger `body.messageBody` / `messageBody` into one input.
  // Built lazily because it only runs when the delete confirmation opens.
  const getParameterReferenceCount = useCallback((paramName: string) => {
    const state = useNodeEditorStore.getState();
    const editedNode = state.node;
    const draftAction = buildSerializedNodeData({
      nodeData: (editedNode?.nodeData ?? {}) as Record<string, unknown>,
      nodeType: editedNode?.type ?? 'tool',
      operationType: editedNode?.nodeData?.type,
      parameterValues: state.parameterValues,
      settingsValues: state.settingsValues,
      parameterDefinitions: state.parameterDefinitions ?? undefined,
      swaggerOperationMetadata: state.swaggerOperationMetadata ?? undefined,
      pickerDisplayValues: state.pickerDisplayValues,
    });
    const toolActions = (editedNode?.toolData?.actions ?? {}) as Record<string, unknown>;
    const otherActions = Object.entries(toolActions)
      .filter(([name]) => name !== editedNode?.innerActionName)
      .map(([, action]) => action);
    return countAgentParameterReferences(paramName, [draftAction, ...otherActions]);
  }, []);

  return (
    <div className={styles.container}>
      {/* Tool Name Section */}
      <div className={styles.section}>
        <Field
          label={
            <InfoLabel
              info="The name the agent uses to identify and call this tool. Use a descriptive name like 'SendEmail' or 'SearchDocuments'."
            >
              Tool Name
            </InfoLabel>
          }
          required
          {...(hasUnappliedNodeCode && {
            validationState: 'warning' as const,
            validationMessage:
              'Apply or revert the code changes before renaming this tool.',
          })}
        >
          <Input
            value={toolName}
            onChange={handleToolNameChange}
            placeholder="e.g., SendEmail"
            disabled={isToolNameLocked}
          />
        </Field>
        <Text className={styles.hint}>
          This name is shown to the AI agent when it decides which tool to use.
        </Text>
      </div>

      {/* Target Workflow Section — only for sub-workflow / handoff tools */}
      {isSubworkflowTool && (
        <div className={styles.section}>
          <Field
            label={
              <InfoLabel
                info="The workflow this tool hands off to. The agent will invoke this workflow when it decides to use this tool."
              >
                Target Workflow
              </InfoLabel>
            }
            required
          >
            <div className={styles.workflowFieldRow}>
              <ArrowFlowUpRightRegular className={styles.workflowIcon} />
              {isWorkflowsLoading ? (
                <Spinner size="tiny" label="Loading workflows..." />
              ) : (
                <SearchableCombobox
                  className={styles.workflowCombobox}
                  options={workflowOptions}
                  value={targetWorkflowId ?? ''}
                  onChange={handleTargetWorkflowChange}
                  placeholder="Select a workflow..."
                  aria-label="Target workflow"
                />
              )}
            </div>
          </Field>
          <Text className={styles.hint}>
            Change which workflow is invoked when the agent uses this handoff tool.
          </Text>
        </div>
      )}

      {/* Tool Description Section */}
      <div className={styles.section}>
        <Field
          label={
            <InfoLabel
              info="A clear description helps the agent understand when and how to use this tool. Be specific about what the tool does."
            >
              Description
            </InfoLabel>
          }
        >
          <Textarea
            value={toolDescription ?? ''}
            onChange={handleDescriptionChange}
            placeholder="Describe what this tool does and when the agent should use it..."
            rows={2}
            resize="vertical"
          />
        </Field>
        <Text className={styles.hint}>
          Good descriptions improve how accurately the agent uses this tool.
        </Text>
      </div>

      {/* Agent Parameters Section */}
      <AgentParameterEditor
        schema={agentParameterSchema}
        onChange={handleSchemaChange}
        getReferenceCount={getParameterReferenceCount}
        readOnly={isReadOnly}
      />

      <Divider data-testid="tool-parameters-divider" />
    </div>
  );
};

ToolConfigTab.displayName = 'ToolConfigTab';
